import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Camera, Mic, HelpCircle, X, Play } from 'lucide-react';
import config from '../config/siteConfig.json';
import { Button } from '../components/ui/button';
import CountdownTimer from '../components/CountdownTimer';
import { trackMediaActivity } from '../lib/mediaTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { boda, portada, video, acciones, botones, iconos } = config;
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [eventoConfig, setEventoConfig] = useState(null);
  
  // Estado para portada dinámica
  const [portadaConfig, setPortadaConfig] = useState(null);
  const [fotosCarrusel, setFotosCarrusel] = useState([]);
  const [fotoEstatica, setFotoEstatica] = useState(null);
  const [videoPortada, setVideoPortada] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Estado para textos dinámicos
  const [textosConfig, setTextosConfig] = useState(null);

  useEffect(() => {
    fetchEventoConfig();
    fetchPortadaConfig();
    fetchPortadaMedia();
    fetchTextosConfig();
  }, []);

  const fetchTextosConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/config/textos`);
      if (response.ok) {
        const data = await response.json();
        setTextosConfig(data);
      }
    } catch (error) {
      console.error('Error fetching textos config:', error);
    }
  };

  // Helpers para nombres y conector entre nombres (portada)
  const getConectorNombres = () => {
    const conector = textosConfig?.conector_nombres;
    if (conector === '' || conector === undefined || conector === null) return '\u00A0';
    return conector;
  };
  const getNombrePrimario = () => {
    const orden = textosConfig?.orden_novios || 'novia_primero';
    return orden === 'novio_primero'
      ? (textosConfig?.nombre_novio || boda.nombreNovio)
      : (textosConfig?.nombre_novia || boda.nombreNovia);
  };
  const getNombreSecundario = () => {
    const orden = textosConfig?.orden_novios || 'novia_primero';
    return orden === 'novio_primero'
      ? (textosConfig?.nombre_novia || boda.nombreNovia)
      : (textosConfig?.nombre_novio || boda.nombreNovio);
  };
  const offsetStyle = (key) => {
    // En móvil usa el offset móvil; si es 0/undefined, usa el de escritorio.
    // En PC usa el offset escritorio.
    const desktop = textosConfig?.[`${key}_offset_y`] ?? 0;
    const mobile = textosConfig?.[`${key}_offset_y_movil`] ?? 0;
    const v = isMobile ? mobile : desktop;
    return v ? { transform: `translateY(${v}px)` } : {};
  };

  // Devuelve el tamaño de fuente correcto según viewport
  // (usa `tamano_movil` si está definido y estamos en móvil, si no `tamano`).
  const fontSizePx = (estilo, fallbackPx) => {
    if (!estilo) return fallbackPx;
    if (isMobile && estilo.tamano_movil) return `${estilo.tamano_movil}px`;
    if (estilo.tamano) return `${estilo.tamano}px`;
    return fallbackPx;
  };

  // Convierte color hex (#RRGGBB) + opacidad (0-100) a rgba string
  const hexToRgba = (hex, opacidad) => {
    if (!hex || typeof hex !== 'string') return null;
    const h = hex.replace('#', '');
    if (h.length !== 6) return null;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    const a = Math.max(0, Math.min(100, Number(opacidad) || 0)) / 100;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  // Estilo de los botones de información (fecha/ubicación) - fondo, padding
  const getBotonInfoStyle = () => {
    const colorHex = textosConfig?.botones_info_color;
    const opacidad = textosConfig?.botones_info_opacidad;
    const computedBg = (colorHex && opacidad !== undefined && opacidad !== null)
      ? hexToRgba(colorHex, opacidad)
      : null;
    const padX = textosConfig?.botones_info_padding_x ?? 24;
    const padY = textosConfig?.botones_info_padding_y ?? 12;
    return {
      backgroundColor: computedBg || textosConfig?.color_botones_info || 'rgba(255,255,255,0.3)',
      color: textosConfig?.color_botones_info_texto || '#FFFFFF',
      paddingLeft: `${padX}px`,
      paddingRight: `${padX}px`,
      paddingTop: `${padY}px`,
      paddingBottom: `${padY}px`,
    };
  };

  // Estilo del texto interno de los botones de información
  const getBotonInfoTextStyle = () => {
    const e = textosConfig?.botones_info_estilo;
    const sizePx = e
      ? (isMobile && e.tamano_movil ? `${e.tamano_movil}px` : (e.tamano ? `${e.tamano}px` : '16px'))
      : '16px';
    return {
      fontFamily: e?.fuente || 'Montserrat',
      fontSize: sizePx,
      fontWeight: e?.negrita ? 'bold' : '500',
      fontStyle: e?.cursiva ? 'italic' : 'normal',
    };
  };

  const fetchEventoConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/evento/config`);
      if (response.ok) {
        const data = await response.json();
        setEventoConfig(data);
      }
    } catch (error) {
      console.error('Error fetching evento config:', error);
    }
  };

  const fetchPortadaConfig = async () => {
    try {
      // Usar endpoint público
      const response = await fetch(`${API_URL}/api/portada/config`);
      if (response.ok) {
        const data = await response.json();
        setPortadaConfig(data);
      }
    } catch (error) {
      console.error('Error fetching portada config:', error);
    }
  };

  const fetchPortadaMedia = async () => {
    try {
      const [fotosRes, fotoEstaticaRes, videoRes] = await Promise.all([
        fetch(`${API_URL}/api/portada/fotos`),
        fetch(`${API_URL}/api/portada/foto-estatica`),
        fetch(`${API_URL}/api/portada/video`)
      ]);
      if (fotosRes.ok) {
        const fotos = await fotosRes.json();
        setFotosCarrusel(fotos || []);
      }
      if (fotoEstaticaRes.ok) {
        const data = await fotoEstaticaRes.json();
        setFotoEstatica(data && data.url ? data : null);
      }
      if (videoRes.ok) {
        const data = await videoRes.json();
        setVideoPortada(data && data.url ? data : null);
      }
    } catch (error) {
      console.error('Error fetching portada media:', error);
    }
  };

  // Carrusel automático para la sección de foto de pareja (solo en modo carrusel)
  useEffect(() => {
    if (portadaConfig?.foto_2_modo === 'carrusel' && 
        portadaConfig?.carrusel_auto && 
        fotosCarrusel.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % fotosCarrusel.length);
      }, (portadaConfig.carrusel_segundos || 5) * 1000);
      return () => clearInterval(interval);
    }
  }, [portadaConfig, fotosCarrusel.length]);

  // Track carrusel: registra view_thumb una vez por foto mostrada
  useEffect(() => {
    if (!fotosCarrusel || fotosCarrusel.length === 0) return;
    const foto = fotosCarrusel[currentSlide];
    if (!foto?.url) return;
    trackMediaActivity({
      accion: 'view_thumb',
      origen: 'carrusel',
      archivo_url: foto.url,
      archivo_nombre: (foto.url || '').split('/').pop(),
      archivo_tipo: 'foto',
    });
  }, [currentSlide, fotosCarrusel]);

  // Track foto estática de portada (vista cuando se carga)
  useEffect(() => {
    if (fotoEstatica?.url) {
      trackMediaActivity({
        accion: 'view_thumb',
        origen: 'carrusel',
        archivo_url: fotoEstatica.url,
        archivo_nombre: (fotoEstatica.url || '').split('/').pop(),
        archivo_tipo: 'foto',
      });
    }
  }, [fotoEstatica]);

  // Track video de portada (cuando hay URL disponible)
  useEffect(() => {
    if (videoPortada?.url) {
      trackMediaActivity({
        accion: 'view_thumb',
        origen: 'carrusel',
        archivo_url: videoPortada.url,
        archivo_nombre: (videoPortada.url || '').split('/').pop(),
        archivo_tipo: 'video',
      });
    }
  }, [videoPortada]);

  // Funciones para navegación manual (diapositivas)
  const goToPrevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + fotosCarrusel.length) % fotosCarrusel.length);
  };

  const goToNextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % fotosCarrusel.length);
  };

  // Obtener fecha y hora del evento
  const getFechaEvento = () => {
    return eventoConfig?.horarios?.fecha_evento || boda.fechaBoda;
  };

  const getHoraEvento = () => {
    return eventoConfig?.horarios?.hora_inicio || boda.horaCeremoniaCivil;
  };

  const getFechaTexto = () => {
    const fecha = getFechaEvento();
    const date = new Date(fecha + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    }).replace(/\//g, '-');
  };

  // Obtener ubicaciones publicadas
  const getUbicacionesPublicadas = () => {
    if (!eventoConfig?.ubicaciones) return [];
    const ubicaciones = [];
    const { ceremonia, banquete, fiesta } = eventoConfig.ubicaciones;
    if (ceremonia?.publicar && ceremonia?.nombre) ubicaciones.push({ tipo: 'ceremonia', ...ceremonia });
    if (banquete?.publicar && banquete?.nombre) ubicaciones.push({ tipo: 'banquete', ...banquete });
    if (fiesta?.publicar && fiesta?.nombre) ubicaciones.push({ tipo: 'fiesta', ...fiesta });
    return ubicaciones;
  };

  const ubicacionesPublicadas = getUbicacionesPublicadas();
  const primerLugar = ubicacionesPublicadas[0];

  // Estado para detectar móvil (viewport < 768 px) y aplicar valores específicos.
  // Esto se actualiza en tiempo real al rotar el dispositivo o redimensionar la ventana.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Estilos para FOTO PRINCIPAL (hero - siempre estática)
  // Si la pantalla es móvil y existen valores específicos para móvil, se usan;
  // si no, se aplican los valores de escritorio (compatibilidad hacia atrás).
  const getFotoPrincipalStyles = () => {
    const zoomDesktop = portadaConfig?.foto_principal_zoom ?? 100;
    const posXDesktop = portadaConfig?.foto_principal_pos_x ?? 50;
    const posYDesktop = portadaConfig?.foto_principal_pos_y ?? 50;
    if (isMobile) {
      return {
        zoom: portadaConfig?.foto_principal_zoom_movil ?? zoomDesktop,
        posX: portadaConfig?.foto_principal_pos_x_movil ?? posXDesktop,
        posY: portadaConfig?.foto_principal_pos_y_movil ?? posYDesktop,
      };
    }
    return { zoom: zoomDesktop, posX: posXDesktop, posY: posYDesktop };
  };
  
  const fotoPrincipalUrl = `${API_URL}/api/uploads/fotos_portada/foto-portada.jpeg`;

  // Obtener imagen para FOTO DE PAREJA (puede ser estática, carrusel, diapositivas o video)
  const getFotoParejaImage = () => {
    const modo = portadaConfig?.foto_2_modo || 'estatica';
    
    // Modo carrusel o diapositivas: usar las fotos del carrusel
    if ((modo === 'carrusel' || modo === 'diapositivas') && fotosCarrusel.length > 0) {
      return `${API_URL}${fotosCarrusel[currentSlide]?.url}`;
    }
    
    // Modo estática o video: usar la foto estática subida
    if (fotoEstatica?.url) {
      return `${API_URL}${fotoEstatica.url}`;
    }
    
    // Fallback a imagen por defecto
    return '/assets/images/foto-pareja.jpg';
  };

  // Estilos para FOTO DE PAREJA
  const getFotoParejaStyles = () => {
    const zoom = portadaConfig?.foto_2_zoom || 100;
    const posX = portadaConfig?.foto_2_pos_x || 50;
    const posY = portadaConfig?.foto_2_pos_y || 50;
    
    return {
      objectFit: 'cover',
      objectPosition: `${posX}% ${posY}%`,
      transform: `scale(${zoom / 100})`
    };
  };

  const addToCalendar = () => {
    const fecha = getFechaEvento();
    const hora = getHoraEvento();
    const event = {
      title: `Boda de ${boda.nombreNovia} y ${boda.nombreNovio}`,
      start: `${fecha}T${hora}:00`,
      location: primerLugar?.direccion || boda.direccionRestaurante,
      description: `Celebración de boda`
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${event.start.replace(/[-:]/g, '')}
SUMMARY:${event.title}
LOCATION:${event.location}
DESCRIPTION:${event.description}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'boda.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowCalendarModal(false);
  };

  const openMapsWithGPS = (gps) => {
    if (gps) {
      const coords = gps.replace(/\s/g, '');
      window.open(`https://www.google.com/maps/search/?api=1&query=${coords}`, '_blank');
    }
  };

  const openMaps = (direccion) => {
    const address = encodeURIComponent(direccion || boda.direccionRestaurante);
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  const getIcon = (iconName) => {
    const icons = {
      'users': Users,
      'camera': Camera,
      'mic': Mic,
      'help-circle': HelpCircle
    };
    return icons[iconName] || Users;
  };

  const actionCardsAll = [
    {
      icon: iconos.iconoConfirmar,
      title: config.navegacion.confirmar,
      description: 'Dinos si podrás acompañarnos en este día especial',
      link: '/confirmacion',
      visible: textosConfig?.mostrar_confirmar_home !== false,
    },
    {
      icon: iconos.iconoGaleria,
      title: config.navegacion.galeria,
      description: 'Comparte tus fotos y videos de la celebración',
      link: '/galeria',
      visible: textosConfig?.mostrar_galeria_home !== false,
    },
    {
      icon: iconos.iconoMensajes,
      title: config.navegacion.mensajes,
      description: 'Deja un mensaje especial para los novios',
      link: '/mensajes',
      visible: textosConfig?.mostrar_mensajes_home !== false,
    },
    {
      icon: iconos.iconoConcurso,
      title: config.navegacion.concurso,
      description: 'Participa y demuestra cuánto sabes de nosotros',
      link: '/concurso',
      visible: textosConfig?.mostrar_concurso_home !== false,
    }
  ];
  const actionCards = actionCardsAll.filter((c) => c.visible);

  const modoFotoPareja = portadaConfig?.foto_2_modo || 'estatica';
  const tieneVideo = modoFotoPareja === 'video' && videoPortada?.url;

  return (
    <div className="min-h-screen">
      {/* ========== HERO SECTION - FOTO PRINCIPAL (siempre estática) ========== */}
      {/* v73.49 — Antes: `items-center` centraba todo el contenido verticalmente,
          haciendo que al reducir la altura de la ventana el saludo/titular se
          ocultaran detrás del header. Ahora `items-start` ancla el contenido
          arriba (con padding-top que respeta el header), de modo que al
          reducir la altura SOLO se recorta lo de abajo (botones, countdown). */}
      <section className="relative min-h-[100svh] flex items-start justify-center overflow-hidden pt-24 md:pt-28">
        {/* Imagen de fondo - FOTO PRINCIPAL con zoom y posición */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0"
            style={{
              transform: `scale(${getFotoPrincipalStyles().zoom / 100})`,
              transformOrigin: `${getFotoPrincipalStyles().posX}% ${getFotoPrincipalStyles().posY}%`
            }}
          >
            <img loading="lazy" decoding="async" 
              src={fotoPrincipalUrl}
              alt="Foto principal"
              className="w-full h-full object-cover"
              style={{
                objectPosition: `${getFotoPrincipalStyles().posX}% ${getFotoPrincipalStyles().posY}%`
              }}
            />
          </div>
          {/* Overlay oscuro */}
          <div className="absolute inset-0 bg-black bg-opacity-30" />
        </div>
        
        {/* Contenido del Hero */}
        <div className="relative z-10 text-center text-white px-4 py-8 w-full">
          <div style={offsetStyle('saludo')}>
            <p 
              className="mb-6 animate-fade-in px-4"
              style={{
                fontFamily: textosConfig?.saludo_bienvenida_estilo?.fuente || portada.textoBienvenidaFuente,
                fontSize: fontSizePx(textosConfig?.saludo_bienvenida_estilo, portada.textoBienvenidaTamano),
                color: textosConfig?.color_saludo || '#FFFFFF',
                fontWeight: textosConfig?.saludo_bienvenida_estilo?.negrita ? 'bold' : (portada.textoBienvenidaNegrita === 'si' ? 'bold' : 'normal'),
                fontStyle: textosConfig?.saludo_bienvenida_estilo?.cursiva ? 'italic' : 'normal',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              }}
            >
              {textosConfig?.saludo_bienvenida || 'Bienvenidos a nuestra boda'}
            </p>
          </div>
          
          <div style={offsetStyle('titular')}>
            <h1 
              className="mb-8 animate-fade-in-delay-1"
              style={{
                fontFamily: textosConfig?.titular_estilo?.fuente || portada.textoAnuncioFuente,
                fontSize: fontSizePx(textosConfig?.titular_estilo, portada.textoAnuncioTamano),
                color: textosConfig?.color_titular || '#FFFFFF',
                fontWeight: textosConfig?.titular_estilo?.negrita ? 'bold' : 'normal',
                fontStyle: textosConfig?.titular_estilo?.cursiva ? 'italic' : 'normal',
                textShadow: '3px 3px 6px rgba(0,0,0,0.6)',
                letterSpacing: '2px',
                textAlign: textosConfig?.titular_estilo?.posicion || 'center',
              }}
            >
              {textosConfig?.titular || portada.textoAnuncio}
            </h1>
          </div>
          
          {/* Nombres - Con opción de formato vertical en móvil */}
          {textosConfig?.nombres_formato_vertical ? (
            <div style={offsetStyle('nombres')}>
              {/* Versión móvil: vertical */}
              <div 
                className="mb-12 animate-fade-in-delay-2 flex flex-col items-center md:hidden"
                style={{
                  fontFamily: textosConfig?.nombres_estilo?.fuente || portada.nombresFuente,
                  fontSize: fontSizePx(textosConfig?.nombres_estilo, portada.nombresTamano),
                  color: textosConfig?.color_nombres || '#FFFFFF',
                  fontWeight: textosConfig?.nombres_estilo?.negrita ? 'bold' : (portada.nombresNegrita === 'si' ? 'bold' : 'normal'),
                  fontStyle: textosConfig?.nombres_estilo?.cursiva ? 'italic' : 'normal',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                <span>{getNombrePrimario()}</span>
                <span className="my-1" style={{ fontSize: '0.6em' }}>
                  {getConectorNombres()}
                </span>
                <span>{getNombreSecundario()}</span>
              </div>
              {/* Versión PC: horizontal */}
              <h2 
                className="mb-12 animate-fade-in-delay-2 hidden md:block"
                style={{
                  fontFamily: textosConfig?.nombres_estilo?.fuente || portada.nombresFuente,
                  fontSize: fontSizePx(textosConfig?.nombres_estilo, portada.nombresTamano),
                  color: textosConfig?.color_nombres || '#FFFFFF',
                  fontWeight: textosConfig?.nombres_estilo?.negrita ? 'bold' : (portada.nombresNegrita === 'si' ? 'bold' : 'normal'),
                  fontStyle: textosConfig?.nombres_estilo?.cursiva ? 'italic' : 'normal',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                {getNombrePrimario()} {getConectorNombres()} {getNombreSecundario()}
              </h2>
            </div>
          ) : (
            <div style={offsetStyle('nombres')}>
              <h2 
                className="mb-12 animate-fade-in-delay-2"
                style={{
                  fontFamily: textosConfig?.nombres_estilo?.fuente || portada.nombresFuente,
                  fontSize: fontSizePx(textosConfig?.nombres_estilo, portada.nombresTamano),
                  color: textosConfig?.color_nombres || '#FFFFFF',
                  fontWeight: textosConfig?.nombres_estilo?.negrita ? 'bold' : (portada.nombresNegrita === 'si' ? 'bold' : 'normal'),
                  fontStyle: textosConfig?.nombres_estilo?.cursiva ? 'italic' : 'normal',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                {getNombrePrimario()} {getConectorNombres()} {getNombreSecundario()}
              </h2>
            </div>
          )}

          {/* Fecha y Lugar */}
          <div style={offsetStyle('botones_info')}>
            <div 
              className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mb-12 animate-fade-in-delay-3 px-4"
            >
              {(textosConfig?.mostrar_boton_fecha !== false) && (
                <button
                  onClick={() => setShowCalendarModal(true)}
                  data-testid="btn-info-fecha"
                  className="flex items-center gap-3 backdrop-blur-sm rounded-full hover:opacity-80 transition-all duration-300"
                  style={getBotonInfoStyle()}
                >
                  <Calendar className="w-5 h-5 md:w-6 md:h-6" />
                  <span style={getBotonInfoTextStyle()}>{getFechaTexto()}</span>
                </button>
              )}
              
              {(textosConfig?.mostrar_boton_ubicacion !== false) && (
                <button
                  onClick={() => setShowLocationModal(true)}
                  data-testid="btn-info-ubicacion"
                  className="flex items-center gap-3 backdrop-blur-sm rounded-full hover:opacity-80 transition-all duration-300"
                  style={getBotonInfoStyle()}
                >
                  <MapPin className="w-5 h-5 md:w-6 md:h-6" />
                  <span style={getBotonInfoTextStyle()}>
                    {primerLugar?.nombre || boda.lugarBoda}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Contador Regresivo - Solo si está habilitado */}
          {(textosConfig?.mostrar_cuenta_atras !== false) && (
            <div style={offsetStyle('contador')}>
              <CountdownTimer targetDate={getFechaEvento()} textColor={textosConfig?.color_contador} />
            </div>
          )}

          {/* Botón CTA */}
          {(textosConfig?.mostrar_boton_confirmar !== false) && (
            <div style={offsetStyle('boton_confirmar')}>
              <Link to="/confirmacion">
                <Button 
                  data-testid="btn-confirmar-hero"
                  className="animate-fade-in-delay-4 hover:scale-105 transition-transform duration-300 shadow-lg"
                  style={{
                    backgroundColor: textosConfig?.color_boton_confirmar || botones.colorBotonConfirmacion,
                    color: textosConfig?.color_boton_confirmar_texto || '#FFFFFF',
                    fontSize: botones.textoBotonTamano,
                    fontFamily: botones.textoBotonFuente,
                    padding: '24px 48px',
                    borderRadius: '50px'
                  }}
                >
                  {botones.textoBotonConfirmacion}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ========== SECCIÓN FOTO DE PAREJA (estática/carrusel/diapositivas/video) ========== */}
      <section className="py-20 px-4" style={{ backgroundColor: '#FDFCFA' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Contenedor de la foto/carrusel/diapositivas/video */}
            <div 
              className="relative rounded-lg overflow-hidden shadow-2xl cursor-pointer group"
              onClick={() => tieneVideo && setShowVideoModal(true)}
            >
              {/* Imagen (estática, carrusel o diapositivas) */}
              <div className="relative overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={getFotoParejaImage()}
                  alt="Wendy y Jose"
                  className="w-full h-auto transition-transform duration-1000"
                  style={getFotoParejaStyles()}
                />
                
                {/* Icono de play si es modo video */}
                {tieneVideo && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-10 h-10 text-gray-800 ml-1" />
                    </div>
                  </div>
                )}
                
                {/* Flechas de navegación para modo diapositivas */}
                {modoFotoPareja === 'diapositivas' && fotosCarrusel.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        goToPrevSlide();
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-lg transition-all opacity-70 hover:opacity-100"
                      aria-label="Anterior"
                    >
                      <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        goToNextSlide();
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-lg transition-all opacity-70 hover:opacity-100"
                      aria-label="Siguiente"
                    >
                      <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Indicadores del carrusel o diapositivas */}
              {(modoFotoPareja === 'carrusel' || modoFotoPareja === 'diapositivas') && fotosCarrusel.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {fotosCarrusel.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentSlide(index);
                      }}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentSlide ? 'bg-white scale-125' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Texto de presentación */}
            <div style={{ 
              width: textosConfig?.historia_ancho ? `${textosConfig.historia_ancho}%` : '100%',
              margin: '0 auto'
            }}>
              <p 
                className="leading-relaxed"
                style={{
                  fontFamily: textosConfig?.historia_estilo?.fuente || video.textoPresentacionFuente,
                  fontSize: fontSizePx(textosConfig?.historia_estilo, video.textoPresentacionTamano),
                  color: textosConfig?.color_historia || video.textoPresentacionColor,
                  lineHeight: '1.8',
                  fontWeight: textosConfig?.historia_estilo?.negrita ? 'bold' : 'normal',
                  fontStyle: textosConfig?.historia_estilo?.cursiva ? 'italic' : 'normal',
                  textAlign: textosConfig?.historia_estilo?.posicion || 'left'
                }}
              >
                {textosConfig?.historia || video.textoPresentacion}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECCIÓN DE ACCIONES ========== */}
      {actionCards.length > 0 ? (
      <section className="py-20 px-4" style={{ backgroundColor: '#F5F1E8' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 
              className="mb-4"
              style={{
                fontFamily: textosConfig?.gancho_estilo?.fuente || acciones.sigueAdelanteFuente,
                fontSize: fontSizePx(textosConfig?.gancho_estilo, acciones.sigueAdelanteTamano),
                color: textosConfig?.color_gancho || acciones.sigueAdelanteColor,
                fontWeight: textosConfig?.gancho_estilo?.negrita ? 'bold' : 'bold',
                fontStyle: textosConfig?.gancho_estilo?.cursiva ? 'italic' : 'normal'
              }}
            >
              {textosConfig?.gancho || acciones.sigueAdelante}
            </h2>
            <p 
              style={{
                fontFamily: textosConfig?.gancho2_estilo?.fuente || acciones.animateFuente,
                fontSize: fontSizePx(textosConfig?.gancho2_estilo, acciones.animateTamano),
                color: textosConfig?.color_gancho2 || acciones.animateColor,
                fontWeight: textosConfig?.gancho2_estilo?.negrita ? 'bold' : 'normal',
                fontStyle: textosConfig?.gancho2_estilo?.cursiva ? 'italic' : 'normal'
              }}
            >
              {textosConfig?.gancho2 || acciones.animate}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {actionCards.map((card, index) => {
              const IconComponent = getIcon(card.icon);
              return (
                <Link 
                  key={index}
                  to={card.link}
                  className="group"
                >
                  <div className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div 
                      className="mx-auto mb-6 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                      style={{
                        width: acciones.tamanoCirculoIcono,
                        height: acciones.tamanoCirculoIcono,
                        backgroundColor: acciones.colorCirculoIcono
                      }}
                    >
                      <IconComponent className="w-10 h-10 text-white" />
                    </div>
                    <h3 
                      className="mb-3"
                      style={{
                        fontSize: acciones.tamanoTextosPrincipales,
                        color: acciones.colorTextosPrincipales,
                        fontWeight: '600'
                      }}
                    >
                      {card.title}
                    </h3>
                    <p 
                      style={{
                        fontSize: acciones.tamanoDescripciones,
                        color: acciones.colorDescripciones,
                        lineHeight: '1.6'
                      }}
                    >
                      {card.description}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      ) : (
        textosConfig?.bloque_alternativo_activo !== false && (
          <section className="py-20 px-4" style={{ backgroundColor: '#F5F1E8' }} data-testid="bloque-alternativo">
            <div className="container mx-auto max-w-3xl text-center">
              <div className="text-7xl mb-6" aria-hidden="true">
                {textosConfig?.bloque_alternativo_icono || '⏳'}
              </div>
              <h2 className="text-4xl md:text-5xl font-light mb-4"
                style={{ fontFamily: 'Playfair Display', color: '#5C5C5C' }}>
                {textosConfig?.bloque_alternativo_titulo || 'Pronto te esperamos'}
              </h2>
              <p className="text-base md:text-lg leading-relaxed whitespace-pre-line"
                style={{ color: '#7A7A7A' }}>
                {textosConfig?.bloque_alternativo_texto ||
                  'Estamos preparando todos los detalles. Muy pronto podrás participar de la celebración desde aquí.'}
              </p>
            </div>
          </section>
        )
      )}

      {/* ========== MODAL DE VIDEO ========== */}
      {showVideoModal && tieneVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 animate-fade-in"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="w-8 h-8" />
            </button>
            <video
              autoPlay
              controls
              className="w-full rounded-lg shadow-2xl"
              onPlay={() => trackMediaActivity({
                accion: 'view_full',
                origen: 'carrusel',
                archivo_url: videoPortada.url,
                archivo_nombre: (videoPortada.url || '').split('/').pop(),
                archivo_tipo: 'video',
              })}
            >
              <source src={`${API_URL}${videoPortada.url}`} type="video/mp4" />
            </video>
          </div>
        </div>
      )}

      {/* ========== MODAL DE CALENDARIO ========== */}
      {showCalendarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowCalendarModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <Calendar className="w-16 h-16 mx-auto mb-6" style={{ color: '#D4846D' }} />
              <h3 className="text-2xl font-semibold mb-4" style={{ color: '#5C5C5C', fontFamily: 'Playfair Display' }}>
                {boda.nombreNovia} {boda.textoUnion} {boda.nombreNovio}
              </h3>
              <p className="text-lg mb-2" style={{ color: '#5C5C5C' }}>
                {getFechaTexto()}
              </p>
              <p className="text-3xl font-bold mb-6" style={{ color: '#D4846D' }}>
                La ceremonia será a las {getHoraEvento()} horas
              </p>

              {/* Mostrar cronograma si está habilitado */}
              {eventoConfig?.horarios?.publicar_seccion && 
               eventoConfig?.horarios?.publicar_portada && 
               eventoConfig?.horarios?.entradas?.length > 0 && (
                <div className={`my-6 border-t pt-6 ${eventoConfig.horarios.portada_alineacion === 'centrado' ? 'text-center' : 'text-left'}`}>
                  <h4 className="font-semibold mb-4 text-center" style={{ color: '#5C5C5C' }}>
                    Cronograma del día
                  </h4>
                  <div className={`space-y-3 ${eventoConfig.horarios.portada_alineacion === 'centrado' ? 'inline-block' : ''}`}>
                    {eventoConfig.horarios.entradas.map((entrada) => (
                      <div 
                        key={entrada.id} 
                        className={`flex items-center gap-3 ${
                          eventoConfig.horarios.portada_posicion_iconos === 'encima' 
                            ? 'flex-col items-center' 
                            : ''
                        }`}
                      >
                        {eventoConfig.horarios.portada_mostrar_iconos && entrada.icono && (
                          <img loading="lazy" decoding="async" 
                            src={`${API_URL}/api/uploads/iconos/${entrada.icono}`} 
                            alt="" 
                            className="w-8 h-8"
                          />
                        )}
                        <div className={`flex items-center gap-2 ${eventoConfig.horarios.portada_posicion_iconos === 'encima' ? 'justify-center' : ''}`}>
                          <span className="font-semibold" style={{ color: '#D4846D' }}>
                            {entrada.hora}
                          </span>
                          <span style={{ color: '#5C5C5C' }}>{entrada.texto}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={addToCalendar}
                className="w-full text-white py-6 text-lg"
                style={{ backgroundColor: '#5C5C5C' }}
              >
                <Calendar className="w-5 h-5 mr-2 inline" />
                Añadir al calendario
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== MODAL DE UBICACIÓN ========== */}
      {showLocationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowLocationModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <MapPin className="w-16 h-16 mx-auto mb-6" style={{ color: '#D4846D' }} />
              
              {ubicacionesPublicadas.length === 0 ? (
                <>
                  <h3 className="text-2xl font-semibold mb-4" style={{ color: '#5C5C5C', fontFamily: 'Playfair Display' }}>
                    {boda.lugarBodaCompleto}
                  </h3>
                  <p className="text-gray-600 mb-2 font-semibold">Dirección:</p>
                  <p className="text-lg mb-8" style={{ color: '#5C5C5C' }}>
                    {boda.direccionRestaurante}
                  </p>
                  <Button
                    onClick={() => {
                      openMaps(boda.direccionRestaurante);
                      setShowLocationModal(false);
                    }}
                    className="w-full text-white py-6 text-lg"
                    style={{ backgroundColor: '#5C5C5C' }}
                  >
                    <MapPin className="w-5 h-5 mr-2 inline" />
                    Ir
                  </Button>
                </>
              ) : ubicacionesPublicadas.length === 1 ? (
                <>
                  <h3 className="text-2xl font-semibold mb-4" style={{ color: '#5C5C5C', fontFamily: 'Playfair Display' }}>
                    {ubicacionesPublicadas[0].nombre}
                  </h3>
                  <p className="text-gray-600 mb-2 font-semibold">Dirección:</p>
                  <p className="text-lg mb-8" style={{ color: '#5C5C5C' }}>
                    {ubicacionesPublicadas[0].direccion}
                  </p>
                  <Button
                    onClick={() => {
                      if (ubicacionesPublicadas[0].gps) {
                        openMapsWithGPS(ubicacionesPublicadas[0].gps);
                      } else {
                        openMaps(ubicacionesPublicadas[0].direccion);
                      }
                      setShowLocationModal(false);
                    }}
                    className="w-full text-white py-6 text-lg"
                    style={{ backgroundColor: '#5C5C5C' }}
                  >
                    <MapPin className="w-5 h-5 mr-2 inline" />
                    Ir
                  </Button>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-semibold mb-6" style={{ color: '#5C5C5C', fontFamily: 'Playfair Display' }}>
                    Ubicaciones del evento
                  </h3>
                  <div className="space-y-4">
                    {ubicacionesPublicadas.map((lugar, index) => (
                      <Button
                        key={index}
                        onClick={() => {
                          if (lugar.gps) {
                            openMapsWithGPS(lugar.gps);
                          } else {
                            openMaps(lugar.direccion);
                          }
                        }}
                        className="w-full text-white py-5 px-4 text-left flex flex-col items-start min-h-[80px] h-auto whitespace-normal"
                        style={{ backgroundColor: '#5C5C5C' }}
                      >
                        <span className="font-semibold text-base mb-1">{lugar.nombre}</span>
                        <span className="text-sm opacity-80 break-words w-full">{lugar.direccion}</span>
                      </Button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
